import React, { ComponentProps, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import highchartsStock from 'highcharts/modules/stock';
import HighchartsExporting from 'highcharts/modules/exporting';
import Highcharts, { merge } from 'highcharts';
import { GetUserAssetResult, getAssetHistory, getPositions } from 'config/api';
import { Side } from 'components/features/utils/side';
import { AssetTypeKey } from 'components/features/utils/assetType';
import { AssetBundleType } from 'components/features/home/Home';
import { ChartForPreviewType } from 'components/features/preview-share/useChartRenderer';
import {
  usePreview,
  UsePreviewProps,
} from 'components/features/preview-share/Preview';
import {
  RenderedChartToElement,
  ChartPreviewState,
  StateController,
  defualtStateForHome,
  stateToOptions,
} from 'components/features/preview-share/PreviewUtils';
import { getDate } from 'components/features/utils/converter';
import Loading from 'components/commons/loading/Loading';
import {
  ProfitAndLossStuffingChart,
  ProfitAndLossStuffingChartInternal,
} from './ProfitAndLossStuffingChart';
import { ProfitAndLossStuffingPosition } from './ProfitAndLossStuffingPosition';
import {
  AssetHistoryItemsProps,
  ExtraTimeSeriesData,
  TimeSeriesDatum,
  getTopixData,
} from './../assetStuffing/AssetStuffing';
import { usePreviewPager } from '../PortfolioStuffing/asset/PortfolioStuffingAssetChart';

highchartsStock(Highcharts);
HighchartsExporting(Highcharts);

export type ProfitAndLossStuffingChartDatum = {
  x: Date;
  y: number | null;
};

export type ProfitAndLossStuffingChartProps = {
  data: ProfitAndLossStuffingChartDatum[];
  extraData?: ExtraTimeSeriesData[];
};

export const ProfitAndLossStuffingChartForPreview: (
  props: ComponentProps<
    ChartForPreviewType<ProfitAndLossStuffingChartProps['data']>
  >
) => React.ReactElement = ({
  data,
  options,
  range,
  setChartDataURL,
  topixData,
}) => {
  return (
    <>
      <ProfitAndLossStuffingChartInternal
        {...{
          data,
          topixData,
          forPreviewSetter: { tag: 'forInPreview', setChartDataURL },
          options: range
            ? merge(options, {
                xAxis: { min: range.min.getTime(), max: range.max.getTime() },
                legend: {
                  enabled: false,
                },
              } as Highcharts.Options)
            : options,
        }}
      />
    </>
  );
};

type ProfitAndLossStuffingProps = {
  asset: GetUserAssetResult;
  assetBundleType: AssetBundleType;
  UserBrokerUpdateTime: React.ReactElement;
  getIsOutOfDate: (arg: { account: string }) => boolean | null;
  toPreview: (element: React.ReactElement) => void;
  backHome: () => void;
};

export type ProfitOrLossItemProps = {
  assetTypeKey: AssetTypeKey;
  side: Side | null;
  link: string | null;
  identifier: string;
  name: string;
  profitOrLossAmount: number | null;
  refresh: { isOutOfDate: boolean; account: string } | null;
  profitOrLossAmountForeign?: number | null;
  profitOrLossAmountForeignUnit?: string;
};

type ChartProps = {
  assetHistory: AssetHistoryItemsProps[];
  asset: GetUserAssetResult;
  UserBrokerUpdateTime: React.ReactElement;
  toPreview: (element: React.ReactElement) => void;
  backHome: () => void;
};

const Chart = ({
  assetHistory,
  asset,
  UserBrokerUpdateTime,
  toPreview,
  backHome,
}: ChartProps): React.ReactElement => {
  const topixData = getTopixData(assetHistory);

  const data = useMemo(
    () =>
      assetHistory.map(asset => {
        return {
          x: getDate(asset.day),
          y: asset.totalProfitOrLoss ?? null,
        };
      }),
    [assetHistory]
  );

  const chartIncorrectData = useMemo(() => {
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: assetHistory.map(it => {
        return {
          value: it.incorrect,
          date: getDate(it.day),
        } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [assetHistory]);

  const props: UsePreviewProps<
    ChartPreviewState,
    ProfitAndLossStuffingChartProps['data']
  > = {
    Chart: ProfitAndLossStuffingChartForPreview,
    defaultState: defualtStateForHome(asset),
    extra: data,
    RenderedChartToElement,
    StateController,
    stateToOptions,
    onCancel: backHome,
    chartTitle: '損益',
  };

  const { preview, TwitterIconPreviewButton, setRange } = usePreview(props);
  usePreviewPager(preview(), backHome, toPreview);

  return (
    <>
      <TwitterIconPreviewButton className="bg-bluegray-600" />
      <div className="pl-4 bg-bluegray-600">
        <ProfitAndLossStuffingChart
          {...{ data, extraData: chartIncorrectData, setRange, topixData }}
        />
      </div>
      <div className="flex flex-row items-center relative z-10 -mt-6 mb-4 bg-bluegray-600">
        {UserBrokerUpdateTime}
      </div>
    </>
  );
};

export type PositionItemsProps = {
  brokerID: string;
  account: string;
  symbol: string;
  symbolName: string;
  accountType: number;
  productType: number;
  cashMargin: number;
  side: number;
  qty: number | null;
  price: number | null;
  currentAmount: number | null;
  currentAmountUSD: number | null;
  profitOrLoss: number | null;
  profitOrLossUSD: number | null;
  validationAmount: number | null;
  diffProfitOrLoss: number | null;
  diffProfitOrLossUSD: number | null;
  diffProfitOrLossRate: number | null;
  currencyUnit: string | null;
  status: string | null;
};

export const ProfitAndlossStuffing = ({
  asset,
  assetBundleType,
  UserBrokerUpdateTime,
  getIsOutOfDate,
  toPreview,
  backHome,
}: ProfitAndLossStuffingProps): React.ReactElement => {
  const {
    data: positions,
    isFetching: positionsIsFetching,
    isRefetching: positionsIsRefetching,
    refetch: positionsRefetch,
  } = useQuery(
    'profitAndLossStuffingPositions',
    () => getPositionsAPI(assetBundleType.key),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: assetHistory, refetch: assetHistoryRefetch } = useQuery(
    'portfolioStuffingAssetHistory',
    () => getAssetHistoryAPI(assetBundleType.key),
    {
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    (async () => {
      await positionsRefetch();
      await assetHistoryRefetch();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetBundleType]);

  const title = '保有資産の損益';

  if (
    positions === null ||
    positions === undefined ||
    assetHistory === null ||
    assetHistory === undefined
  ) {
    return (
      <>
        <LoadingChart />
        <LoadingList />
      </>
    );
  }

  return (
    <>
      {positionsIsFetching && positionsIsRefetching ? (
        <LoadingChart />
      ) : (
        <>
          <Chart
            {...{
              assetHistory: assetHistory as AssetHistoryItemsProps[],
              asset,
              UserBrokerUpdateTime,
              toPreview,
              backHome,
            }}
          />
        </>
      )}
      <ProfitAndLossStuffingPosition
        {...{
          positions: positions as PositionItemsProps[],
          getIsOutOfDate,
          title,
        }}
      />
    </>
  );
};

const LoadingChart = () => {
  return (
    <div className="pl-4 mb-4 bg-bluegray-600">
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '120px',
          paddingBottom: '120px',
        }}
      />
    </div>
  );
};

const LoadingList = () => {
  return (
    <div className="p-4 pb-0 max-w-md mx-auto">
      <div className="text-lg font-bold mb-6">保有資産の損益</div>
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
    </div>
  );
};

const getPositionsAPI = async (key: number) => {
  const groupID = key !== 0 && key !== 9999 ? key.toString() : undefined;

  const resPositions_ = await getPositions(groupID);
  const resPositions = resPositions_.Items.map(item => {
    return {
      brokerID: item.BrokerID,
      account: item.Account,
      symbol: item.Symbol,
      symbolName: item.SymbolName,
      accountType: item.AccountType,
      productType: item.ProductType,
      cashMargin: item.CashMargin,
      side: item.Side,
      qty: item.Qty,
      price: item.Price,
      currentAmount: item.CurrentAmount,
      currentAmountUSD: item.CurrentAmountUSD,
      profitOrLoss: item.ProfitOrLoss,
      profitOrLossUSD: item.ProfitOrLossUSD,
      validationAmount: item.ValidationAmount,
      diffProfitOrLoss: item.DiffProfitOrLoss,
      diffProfitOrLossUSD: item.DiffProfitOrLossUSD,
      currencyUnit: item.CurrencyUnit,
    };
  });
  return resPositions;
};

const getAssetHistoryAPI = async (key: number) => {
  const groupID = key !== 0 && key !== 9999 ? key.toString() : undefined;

  const resAssetHistory_ = await getAssetHistory(
    undefined,
    undefined,
    groupID,
    true
  );
  const resAssetHistory = resAssetHistory_.Items.map(item => {
    return {
      day: item.Day,
      totalAssetAmount: item.TotalAssetAmount,
      diffTotalAssetAmount: item.DiffTotalAssetAmount,
      diffTotalAssetRate: item.DiffTotalAssetRate,
      marginProfitOrLoss: item.MarginProfitOrLoss,
      totalProfitOrLoss: item.TotalProfitOrLoss,
      diffTotalProfitOrLoss: item.DiffTotalProfitOrLoss,
      diffTotalProfitOrLossRate: item.DiffTotalProfitOrLossRate,
      stockAmount: item.StockAmount,
      marginAmount: item.MarginAmount,
      roboAdvisorAmount: item.RoboAdvisorAmount,
      uSStockAmount: item.USStockAmount,
      investmentTrustAmount: item.InvestmentTrustAmount,
      unlistedStockAmount: item.UnlistedStockAmount,
      etfEtnAmount: item.EtfEtnAmount,
      foreignEtfEtnAmount: item.ForeignEtfEtnAmount,
      reitAmount: item.ReitAmount,
      foreignReitAmount: item.ForeignReitAmount,
      bondAmount: item.BondAmount,
      foreignBondAmount: item.ForeignBondAmount,
      ebAmount: item.EbAmount,
      foreignEbAmount: item.ForeignEbAmount,
      realEstateAmount: item.RealEstateAmount,
      cashAmount: item.CashAmount,
      otherAmount: item.OtherAmount,
      incorrect: item.Incorrect,
      topix: item.Topix ?? null,
    };
  });
  return resAssetHistory;
};
